import {
    AI_CREDITS_EXHAUSTED_CODE,
    AI_USER_ACTION,
    AI_USER_HOLDING_REASONS,
    AI_USER_SUPERVISOR_REASON,
    aiUserTurn,
    answerAiUserGuidance,
    askAiUserSupervisor,
    getAiUserConversationDeals,
    getAiUserPendingGuidance,
    getAiUserPaidFollowUps,
    registerAiUserFollowUp,
    completeAiUserFollowUp,
    AI_USER_FOLLOW_UP_PAID_SOCKET_EVENT,
    AI_USER_CHAT_HANDOVER_SOCKET_EVENT,
    getAiUserHandoverCandidates,
    type AiUserPaidFollowUp,
    getAiUserProfile,
    type AiUserActionName,
    type AiUserConversationDeal,
    type AiUserPendingGuidance,
    type AiUserProfile,
    type AiUserSupervisorReason,
    type AiUserToolCall,
    type AiUserTurnMessage,
    type AiUserTurnResult,
} from '@/lib/api/ai-user/ai-user-api'
import { getChatByCompany, getChatWithMessages } from '@/lib/api/atendimento/chat'
import { registerUnsavedWork } from '@/lib/app-version/unsaved-work'
import { useChatDock } from '@/lib/providers/chat-dock-provider'
import { SocketContext } from '@/lib/providers/socket-provider'
import { usePermission } from '@/lib/hooks/use-permission'
import { useAuth } from '@/store/hooks'
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type PropsWithChildren,
} from 'react'
import { useNavigate } from 'react-router-dom'
import {
    allowedTargetStages,
    buildFunnelContext,
    buildTurnStep,
    contactMessagesSince,
    contactImageUrls,
    isAllowedMove,
    lastContactMessageId,
    lastMessageIsFromContact,
} from './conversation-context'
import { loadDiary, saveDiary } from './diary-storage'
import {
    describeAppointmentFindSlots,
    describeAppointmentSchedule,
    executeAppointmentFindSlots,
    executeAppointmentSchedule,
    isAppointmentFindSlotsAction,
    isAppointmentScheduleAction,
} from './appointment-schedule'
import { describeBillingCall, executeBillingAction, isBillingAction } from './billing-actions'
import { describePaymentSimulation, executePaymentSimulation, isPaymentSimulationAction } from './payment-simulation'
import { fetchGuideFiles } from './guides'
import { describeOpenSales, findOpenSales } from './open-sales'
import { describeSchedulingCall, executeSchedulingAction, isSchedulingAction } from './scheduling-actions'
import { getScreenController, onScreenEvent, waitForScreenController } from './screen-bridge'

/**
 * Motor do Modo IA (ver docs/ai-user.md, fase 1).
 *
 * Roda NA ABA do usuário de IA, à vista do supervisor: abre a conversa na tela,
 * lê o período atual, pede a decisão ao modelo e escreve a resposta no campo da
 * conversa. Nada sai sem aprovação nesta fase. Fechar a aba, deslogar ou
 * desligar o computador param tudo — é de propósito.
 *
 * Uma conversa por vez, como uma pessoa. O gatilho é o alerta pessoal de
 * mensagem nova (`chat_message`), que o backend só manda a quem é dono da
 * conversa: membros da carteira e responsáveis pelo lead e pela oportunidade.
 */

export type AiUserStatus = 'idle' | 'running' | 'pausing'

export type AiUserStage = 'opening' | 'thinking' | 'working' | 'moving' | 'typing' | 'awaiting_approval' | 'sending'

export type DiaryKind = 'info' | 'decision' | 'action' | 'warning' | 'error'

export interface DiaryEntry {
    id: number
    at: number
    kind: DiaryKind
    text: string
    chatId?: number
}

export interface QueueItem {
    chatId: number
    chatName?: string
    dueAt: number
    /**
     * Passagem: a conversa acabou de ser recebida (ou foi achada pelo botão "Procurar
     * conversas recebidas"). Ela lê a conversa inteira e assume, qualquer que seja a última
     * mensagem (docs/ai-user.md, "Passagem").
     */
    handover?: boolean
}

export interface CurrentWork {
    chatId: number
    chatName?: string
    stage: AiUserStage
}

/**
 * Resposta pronta esperando o supervisor. Com a trava ligada a resposta fica ESTACIONADA
 * nesta lista e o motor segue para as outras conversas; aprovar põe o envio na vez do
 * motor (a tela é uma só: dois passos não a usam ao mesmo tempo).
 */
export interface PendingApproval {
    id: number
    chatId: number
    chatName?: string
    text: string
    /** Quando ficou pronta (ms). A lista mostra há quanto tempo espera. */
    createdAt: number
}

type ApprovalDecision = 'approve' | 'discard' | 'sent' | 'aborted'

interface AiUserRunnerValue {
    status: AiUserStatus
    queue: QueueItem[]
    current: CurrentWork | null
    /** Respostas estacionadas, da mais antiga para a mais nova. */
    approvals: PendingApproval[]
    /** Perguntas ao supervisor sem resposta. A IA está calada nessas conversas. */
    waiting: AiUserPendingGuidance[]
    diary: DiaryEntry[]
    start: () => void
    pause: () => void
    stopNow: () => void
    /** Procura as conversas recebidas que esperam resposta dela e põe na fila como passagem. */
    findHandovers: () => Promise<void>
    findingHandovers: boolean
    /** Envia (com o texto editado, se houver) ou descarta uma resposta estacionada. */
    decideApproval: (id: number, decision: 'approve' | 'discard', text?: string) => void
    assumeCurrent: () => void
    /** O supervisor responde; a IA retoma a conversa com a orientação. */
    answerGuidance: (guidanceId: number, answer: string) => Promise<boolean>
    /** Enviar só com aprovação do supervisor (preferência deste navegador). */
    requireApproval: boolean
    setRequireApproval: (value: boolean) => void
    /** Funil próprio e carteiras. `null` enquanto carrega. */
    profile: AiUserProfile | null
    profileError: string | null
}

/**
 * Preferência do supervisor: enviar só com aprovação. Fica no navegador porque é
 * de quem está à frente desta tela, não da conta. Padrão: envia sozinho.
 */
const REQUIRE_APPROVAL_STORAGE_KEY = 'ai-user:require-approval'
/** Com envio automático, o texto fica um instante no campo antes de sair: dá para ler e parar. */
const AUTO_SEND_DELAY_MS = 1_500

function readRequireApproval(): boolean {
    try {
        return window.localStorage.getItem(REQUIRE_APPROVAL_STORAGE_KEY) === '1'
    } catch {
        return false
    }
}

/** Espera depois do último alerta da conversa: o contato costuma mandar várias mensagens seguidas. */
const DEBOUNCE_MS = 8_000
/** De quanto em quanto tempo o motor olha a fila. */
const TICK_MS = 1_500
/** Prazo para a tela da conversa abrir e se registrar na ponte. */
const SCREEN_TIMEOUT_MS = 20_000
/** Conversa que não abriu volta para a fila depois disto. */
const RETRY_MS = 60_000
/** Quantas conversas a varredura inicial busca. */
const SWEEP_LIMIT = 50
/**
 * Varredura periódica das conversas "minhas sem resposta" enquanto o Modo IA roda. Mover a
 * conversa para a carteira ou o funil da IA a faz ASSUMIR (owner, 27/10/2026): nem todo
 * caminho de mudança de dono gera alerta (mover a oportunidade de funil não gera), e a
 * visibilidade da IA já é "o que é meu", então a varredura pega todos.
 */
const SWEEP_INTERVAL_MS = 60_000
/** Alertas de que algo passou a ser da IA: varre na hora, sem esperar o minuto. */
const OWNERSHIP_ALERT_KINDS = ['wallet', 'deal_assigned', 'lead_assigned', 'chat_transferred']
const OWNERSHIP_SWEEP_DELAY_MS = 2_000
const DIARY_LIMIT = 200

const ACTIONS: AiUserActionName[] = [
    AI_USER_ACTION.sendMessage,
    AI_USER_ACTION.askSupervisor,
    AI_USER_ACTION.noReply,
    AI_USER_ACTION.schedulingOpen,
    AI_USER_ACTION.schedulingSearchCatalog,
    AI_USER_ACTION.schedulingAddItem,
    AI_USER_ACTION.schedulingRemoveItem,
    AI_USER_ACTION.schedulingChooseVariation,
    AI_USER_ACTION.schedulingImportOrder,
    AI_USER_ACTION.schedulingChoosePartner,
    AI_USER_ACTION.schedulingChooseProfessional,
    AI_USER_ACTION.schedulingApplyCoupon,
    AI_USER_ACTION.schedulingChargeSeparately,
    AI_USER_ACTION.schedulingChooseAddress,
    AI_USER_ACTION.schedulingChoosePayment,
    AI_USER_ACTION.schedulingPayLater,
    AI_USER_ACTION.schedulingSubmit,
    AI_USER_ACTION.schedulingPayOnBillingScreen,
    AI_USER_ACTION.billingSetPayment,
    AI_USER_ACTION.billingSplit,
    AI_USER_ACTION.billingSave,
    AI_USER_ACTION.appointmentSetSchedule,
]

/**
 * Pix copia e cola que voltou do envio do agendamento ou do salvar da cobrança. No
 * pagamento dividido é o da parte em Pix (há uma só).
 */
function pixCodeFromResult(name: string, content: string): string | null {
    if (name !== AI_USER_ACTION.schedulingSubmit && name !== AI_USER_ACTION.billingSave) return null
    try {
        const parsed = JSON.parse(content)
        if (!parsed?.ok) return null
        const codes: string[] = [
            parsed.billing?.pixCopyPaste,
            ...((parsed.payments ?? []) as Array<{ pixCopyPaste?: string | null }>).map(entry => entry.pixCopyPaste),
        ].filter((code): code is string => typeof code === 'string' && code.trim().length > 0)
        return codes[0] ?? null
    } catch {
        return null
    }
}

/**
 * O que um envio do agendamento ou o salvar da cobrança criou: os atendimentos e, quando
 * já existe, a cobrança da venda (é por ela que se sabe que o pagamento entrou).
 */
function saleFromResult(
    name: string,
    content: string,
): { appointment?: { kind: 'EXAM' | 'CONSULTATION' | 'PROCEDURE'; id: number }; billingId?: number } | null {
    if (name !== AI_USER_ACTION.schedulingSubmit && name !== AI_USER_ACTION.billingSave) return null
    try {
        const parsed = JSON.parse(content)
        if (!parsed?.ok) return null
        return {
            appointment: parsed.created ?? undefined,
            billingId: parsed.billing?.id ?? parsed.payments?.[0]?.billingId ?? undefined,
        }
    } catch {
        return null
    }
}

/** O bloco do contexto quando há venda paga nesta conversa: é a vez de mandar a guia. */
function describePaidFollowUps(entries: AiUserPaidFollowUp[]): string {
    if (entries.length === 0) return ''
    // Uma guia por atendimento: no grupo, uma por parceiro.
    const guideCount = entries.reduce((sum, entry) => sum + entry.appointments.length, 0)
    return [
        'PAGAMENTO CONFIRMADO NESTA CONVERSA:',
        'Se o supervisor passou nesta conversa a data e a hora combinadas com o parceiro e elas ainda não ' +
            'estão gravadas no atendimento, grave antes (appointment_set_schedule): a guia sai com a data.',
        ...entries.map(
            entry =>
                `- Cobrança #${entry.billingId}, atendimentos ${entry.appointments.map(item => `#${item.id}`).join(', ')}.`,
        ),
        `Escreva agora ao contato que o pagamento foi confirmado e que ${guideCount > 1 ? `as ${guideCount} guias seguem` : 'a guia segue'} nesta mensagem (send_message). ` +
            'A guia vai anexada sozinha: não escreva link nem nome de arquivo. Diga onde apresentá-la (a recepção do ' +
            'parceiro) e, se a data ainda não foi combinada, não invente data nem horário.',
    ].join('\n')
}

/** Ações cujo resultado volta ao modelo no mesmo passo: as da tela e as consultas. */
function isRoundAction(name: string): boolean {
    return (
        isSchedulingAction(name) ||
        isBillingAction(name) ||
        isAppointmentScheduleAction(name) ||
        isAppointmentFindSlotsAction(name) ||
        isPaymentSimulationAction(name)
    )
}

/**
 * Rodadas de ações na tela dentro de um passo (busca, item, parceiro...). Cada rodada
 * é uma chamada ao modelo; o limite segura um laço que não converge.
 */
const MAX_SCREEN_ROUNDS = 12

/** Rótulo do motivo na pergunta que o supervisor lê em "Esperando você". */
const SUPERVISOR_REASON_LABEL: Record<AiUserSupervisorReason, string> = {
    information: 'Dúvida',
    partner_schedule: 'Horário com parceiro',
    cancellation: 'Cancelamento',
    withdrawal: 'Desistência',
}

/**
 * A pergunta pendente guarda o motivo no rótulo da frente (é o que o supervisor lê), e é
 * por ele que se sabe, mesmo depois de recarregar a aba, se o contato foi avisado
 * (`holding`). Devolve a pergunta sem o rótulo.
 */
function parsePendingQuestion(question: string): { question: string; holding: boolean } {
    for (const reason of Object.values(AI_USER_SUPERVISOR_REASON)) {
        const prefix = `${SUPERVISOR_REASON_LABEL[reason]}: `
        if (question.startsWith(prefix)) {
            return { question: question.slice(prefix.length), holding: AI_USER_HOLDING_REASONS.includes(reason) }
        }
    }
    return { question, holding: false }
}

/** Motivo da pergunta pendente, lido do rótulo da frente (filtro de "Esperando você"). */
export function supervisorReasonOf(question: string): AiUserSupervisorReason {
    for (const reason of Object.values(AI_USER_SUPERVISOR_REASON)) {
        if (question.startsWith(`${SUPERVISOR_REASON_LABEL[reason]}: `)) return reason
    }
    return AI_USER_SUPERVISOR_REASON.information
}

export { SUPERVISOR_REASON_LABEL }

function toSupervisorReason(value: unknown): AiUserSupervisorReason {
    const reasons = Object.values(AI_USER_SUPERVISOR_REASON) as string[]
    return reasons.includes(String(value)) ? (value as AiUserSupervisorReason) : AI_USER_SUPERVISOR_REASON.information
}

/** Com pergunta pendente, tudo menos uma segunda consulta ao supervisor na mesma conversa. */
const WAITING_ACTIONS: AiUserActionName[] = ACTIONS.filter(name => name !== AI_USER_ACTION.askSupervisor)

const AiUserRunnerContext = createContext<AiUserRunnerValue | null>(null)

export function useAiUserRunner(): AiUserRunnerValue {
    const value = useContext(AiUserRunnerContext)
    if (!value) throw new Error('useAiUserRunner fora do AiUserRunnerProvider')
    return value
}

function parseArguments(raw: string): Record<string, unknown> {
    try {
        const parsed = JSON.parse(raw)
        return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
        return {}
    }
}

export function AiUserRunnerProvider({ children }: PropsWithChildren) {
    const { user } = useAuth()
    const { canRead } = usePermission()
    // Simular pagamento é o Simulador de vendas: só com o recurso no perfil e no plano,
    // como para qualquer vendedor (sem ele a rota responde 403).
    const canSimulatePaymentRef = useRef(false)
    canSimulatePaymentRef.current = canRead('financial', 'sales_simulator')
    // Agenda própria: fora de INTERMEDIATION ela mesma acha a vaga na agenda do
    // profissional. Em intermediação quem diz o horário é o parceiro (via supervisor).
    const ownAgendaRef = useRef(false)
    ownAgendaRef.current = (user?.company?.secondarySegment || 'OTHER') !== 'INTERMEDIATION'
    const { socket } = useContext(SocketContext)
    const navigate = useNavigate()
    const { isDockOpen, openDock, openChat, closeChat } = useChatDock()

    const [status, setStatus] = useState<AiUserStatus>('idle')
    const [queue, setQueue] = useState<QueueItem[]>([])
    const [current, setCurrent] = useState<CurrentWork | null>(null)
    const [approvals, setApprovals] = useState<PendingApproval[]>([])
    const [waiting, setWaiting] = useState<AiUserPendingGuidance[]>([])
    // Conversas com pergunta pendente, por conversa. A conversa só volta ao motor
    // quando o contato escreve DEPOIS da pergunta (ver `process`).
    const waitingRef = useRef(new Map<number, AiUserPendingGuidance>())
    // Vendas pagas à espera da guia, por conversa. Vêm do servidor: o pagamento pode ter
    // caído com o Modo IA parado.
    const paidRef = useRef(new Map<number, AiUserPaidFollowUp[]>())
    // Conversas que acabaram de receber orientação do supervisor: a orientação é fato novo,
    // e a conversa anda mesmo que a última mensagem seja o aviso de espera da própria IA.
    const guidedRef = useRef(new Set<number>())
    // O diário sobrevive ao recarregamento da aba (ver `diary-storage.ts`).
    const [diary, setDiary] = useState<DiaryEntry[]>(() =>
        user?.id ? (loadDiary(user.id, DIARY_LIMIT) as DiaryEntry[]) : [],
    )

    // O laço lê refs, não estado: o tick roda num intervalo e veria valores velhos.
    const statusRef = useRef<AiUserStatus>('idle')
    const queueRef = useRef(new Map<number, QueueItem>())
    const busyRef = useRef(false)
    const currentChatIdRef = useRef<number | null>(null)
    const assumedRef = useRef(new Set<number>())
    // Última mensagem do contato que já recebeu decisão, por conversa. Sem isto a
    // mesma conversa era reavaliada a cada Iniciar — uma chamada paga por vez, e às
    // vezes com decisão diferente. Só volta a ela quando o contato escreve de novo.
    const decidedRef = useRef(new Map<number, number>())
    const runIdRef = useRef(0)
    const abortRef = useRef<AbortController | null>(null)
    // Respostas estacionadas: quem decide (Enviar/Descartar na gaveta) resolve a promessa
    // que o passo deixou esperando.
    const parkedRef = useRef(
        new Map<number, { chatId: number; resolve: (decision: ApprovalDecision, text?: string) => void }>(),
    )
    const approvalSeqRef = useRef(0)
    // Dono do motor (a tela): cada passo, ou cada retomada de passo estacionado, ganha uma
    // ficha. Só quem tem a ficha solta o motor; retomadas têm vez antes da fila.
    const motorSeqRef = useRef(0)
    const motorOwnerRef = useRef(0)
    const resumeWaitersRef = useRef(0)
    const diaryIdRef = useRef(diary.reduce((max, entry) => Math.max(max, entry.id), 0))
    // Última mensagem (lastMessageAt) de cada conversa quando a varredura a pôs na fila: a
    // varredura periódica só repõe a conversa se algo mudou (sem isto, cada minuto seria um
    // passo pago por conversa que a IA já decidiu não responder).
    const sweptStampRef = useRef(new Map<number, string>())
    const sweepRef = useRef<((options?: { quiet?: boolean }) => Promise<void>) | null>(null)
    const ownershipSweepTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const [requireApproval, setRequireApprovalState] = useState(readRequireApproval)
    const [profile, setProfile] = useState<AiUserProfile | null>(null)
    const [profileError, setProfileError] = useState<string | null>(null)
    const profileRef = useRef<AiUserProfile | null>(null)
    const requireApprovalRef = useRef(requireApproval)

    const userId = user?.id
    const companyId = user?.companyId

    /**
     * Um usuário de IA trabalha numa aba só: duas abas com o mesmo usuário (a sessão por aba
     * permite) atenderiam as mesmas conversas. Quem inicia segura o lock enquanto não volta a
     * ficar parado.
     */
    const workLockReleaseRef = useRef<(() => void) | null>(null)

    const changeStatus = useCallback((next: AiUserStatus) => {
        statusRef.current = next
        setStatus(next)
        if (next === 'idle') {
            workLockReleaseRef.current?.()
            workLockReleaseRef.current = null
        }
    }, [])

    /** `false` quando outra aba já trabalha com este usuário de IA. Sem Web Locks, não trava. */
    const acquireWorkLock = useCallback(async (): Promise<boolean> => {
        if (workLockReleaseRef.current) return true
        const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined
        if (!locks || userId == null) return true
        return new Promise<boolean>(resolve => {
            void locks.request(`ai-user:${userId}`, { ifAvailable: true }, lock => {
                if (!lock) {
                    resolve(false)
                    return undefined
                }
                resolve(true)
                return new Promise<void>(release => {
                    workLockReleaseRef.current = release
                })
            })
        })
    }, [userId])

    useEffect(() => {
        if (userId) saveDiary(userId, diary)
    }, [diary, userId])

    const log = useCallback((kind: DiaryKind, text: string, chatId?: number) => {
        diaryIdRef.current += 1
        const entry: DiaryEntry = { id: diaryIdRef.current, at: Date.now(), kind, text, chatId }
        // Espelho no console, como os toasts: vale para o suporte (print do console).
        console.info(`[ai-user:${kind}]${chatId ? ` #${chatId}` : ''} ${text}`)
        setDiary(prev => [entry, ...prev].slice(0, DIARY_LIMIT))
    }, [])

    const syncQueue = useCallback(() => {
        setQueue(
            [...queueRef.current.values()].sort((a, b) => a.dueAt - b.dueAt),
        )
    }, [])

    const enqueue = useCallback(
        (chatId: number, delayMs: number, chatName?: string, handover?: boolean) => {
            if (assumedRef.current.has(chatId)) return
            const previous = queueRef.current.get(chatId)
            queueRef.current.set(chatId, {
                chatId,
                chatName: chatName ?? previous?.chatName,
                dueAt: Date.now() + delayMs,
                // Passagem não se perde quando a conversa volta à fila por outro motivo.
                handover: handover || previous?.handover,
            })
            syncQueue()
        },
        [syncQueue],
    )

    // No Modo IA a conversa fica no dock, ao lado do menu recolhido, e a área
    // principal fica livre para as outras telas (agendamento, cobrança). Entra já
    // assim; se o supervisor fechar o dock, a próxima conversa o abre de novo.
    useEffect(() => {
        if (!isDockOpen) openDock()
        // Só na entrada: depois, quem decide é o supervisor.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    // Funil próprio e carteiras: sem funil próprio o Modo IA não começa.
    useEffect(() => {
        let cancelled = false
        void getAiUserProfile().then(response => {
            if (cancelled) return
            if (response.success && response.result) {
                profileRef.current = response.result
                setProfile(response.result)
                setProfileError(null)
            } else {
                setProfileError(response.error || 'Não consegui carregar o funil deste usuário de IA.')
            }
        })
        return () => {
            cancelled = true
        }
    }, [])

    // Perguntas ao supervisor ainda sem resposta: vêm do servidor, então recarregar
    // a aba não as perde e a IA continua calada nessas conversas.
    const loadWaiting = useCallback(async () => {
        const response = await getAiUserPendingGuidance()
        if (!response.success || !Array.isArray(response.result)) return
        waitingRef.current = new Map(response.result.map(entry => [entry.chatId, entry]))
        setWaiting(response.result)
    }, [])
    useEffect(() => {
        void loadWaiting()
    }, [loadWaiting])

    const loadPaid = useCallback(async (): Promise<AiUserPaidFollowUp[]> => {
        const response = await getAiUserPaidFollowUps()
        if (!response.success || !Array.isArray(response.result)) return []
        const byChat = new Map<number, AiUserPaidFollowUp[]>()
        for (const entry of response.result) byChat.set(entry.chatId, [...(byChat.get(entry.chatId) ?? []), entry])
        paidRef.current = byChat
        return response.result
    }, [])


    // Trabalho em andamento segura o recarregamento automático depois de um deploy.
    useEffect(
        () => registerUnsavedWork('ai-user-runner', () => statusRef.current !== 'idle'),
        [],
    )

    // Alerta pessoal de mensagem nova: entra na fila com espera, para juntar as
    // mensagens seguidas do contato num passo só.
    useEffect(() => {
        if (!socket) return
        const onNotification = (notification: any) => {
            if (notification?.scope !== 'PERSONAL') return
            // Conversa, lead, oportunidade ou carteira passou a ser da IA: ela assume na hora.
            if (OWNERSHIP_ALERT_KINDS.includes(notification?.alertKind)) {
                if (statusRef.current !== 'running') return
                if (ownershipSweepTimerRef.current) clearTimeout(ownershipSweepTimerRef.current)
                ownershipSweepTimerRef.current = setTimeout(() => {
                    ownershipSweepTimerRef.current = null
                    void sweepRef.current?.({ quiet: true })
                }, OWNERSHIP_SWEEP_DELAY_MS)
                return
            }
            if (notification?.alertKind !== 'chat_message') return
            const chatId = Number(notification?.metadata?.chatId)
            if (!chatId) return
            enqueue(chatId, DEBOUNCE_MS)
        }
        socket.on('notification:new', onNotification)
        // Venda paga: a conversa volta à fila para a IA mandar a guia.
        const onFollowUpPaid = async (payload: any) => {
            const chatId = Number(payload?.chatId)
            if (!chatId) return
            await loadPaid()
            enqueue(chatId, 0)
        }
        socket.on(AI_USER_FOLLOW_UP_PAID_SOCKET_EVENT, onFollowUpPaid)
        // Conversa movida para a IA (carteira, funil, lead, transferência): ela assume.
        const onHandover = (payload: any) => {
            const chatId = Number(payload?.chatId)
            if (!chatId) return
            log('info', 'Conversa recebida: vou ler o histórico e assumir.', chatId)
            enqueue(chatId, OWNERSHIP_SWEEP_DELAY_MS, undefined, true)
        }
        socket.on(AI_USER_CHAT_HANDOVER_SOCKET_EVENT, onHandover)
        return () => {
            socket.off('notification:new', onNotification)
            socket.off(AI_USER_FOLLOW_UP_PAID_SOCKET_EVENT, onFollowUpPaid)
            socket.off(AI_USER_CHAT_HANDOVER_SOCKET_EVENT, onHandover)
        }
    }, [socket, enqueue, loadPaid, log])

    // Supervisor enviou pelo botão da própria tela: vale como aprovação.
    useEffect(
        () =>
            onScreenEvent(event => {
                if (event.type !== 'chat:sent') return
                // O supervisor escreveu ele mesmo na conversa de uma resposta estacionada:
                // vale como "enviada pelo supervisor". A conversa atual da IA não conta: é o
                // próprio motor enviando.
                if (event.chatId === currentChatIdRef.current) return
                for (const entry of parkedRef.current.values()) {
                    if (entry.chatId === event.chatId) entry.resolve('sent')
                }
            }),
        [],
    )

    const finishCurrent = useCallback(() => {
        currentChatIdRef.current = null
        busyRef.current = false
        setCurrent(null)
        if (statusRef.current === 'pausing') {
            changeStatus('idle')
            log('info', 'Modo IA pausado.')
        }
    }, [changeStatus, log])

    const process = useCallback(
        async (item: QueueItem) => {
            if (!userId) return
            let runId = runIdRef.current
            const controller = new AbortController()
            abortRef.current = controller
            const alive = () => runIdRef.current === runId && !controller.signal.aborted
            const { chatId } = item
            const handover = !!item.handover
            let chatName = item.chatName

            busyRef.current = true
            let motorToken = ++motorSeqRef.current
            motorOwnerRef.current = motorToken
            currentChatIdRef.current = chatId
            setCurrent({ chatId, chatName, stage: 'thinking' })

            /** Solta o motor (a resposta ficou estacionada): a fila segue com as outras conversas. */
            const releaseMotor = () => {
                if (abortRef.current === controller) abortRef.current = null
                if (motorOwnerRef.current === motorToken) finishCurrent()
            }
            /**
             * Retoma a vez no motor para enviar a resposta aprovada. Retomadas passam na
             * frente da fila; este passo vira o atual (Parar já e Assumir valem para ele).
             */
            const acquireMotor = async (): Promise<boolean> => {
                resumeWaitersRef.current += 1
                try {
                    while (busyRef.current) {
                        if (controller.signal.aborted) return false
                        await new Promise(resolve => setTimeout(resolve, 300))
                    }
                } finally {
                    resumeWaitersRef.current -= 1
                }
                if (controller.signal.aborted) return false
                busyRef.current = true
                motorToken = ++motorSeqRef.current
                motorOwnerRef.current = motorToken
                runId = runIdRef.current
                abortRef.current = controller
                currentChatIdRef.current = chatId
                setCurrent({ chatId, chatName, stage: 'sending' })
                return true
            }

            try {
                // Lê antes de abrir: conversa sem nada a fazer não deve fazer a tela
                // pular para ela. Só abre na tela quando vai agir.
                const loaded = await getChatWithMessages(chatId)
                if (!alive()) return
                if (!loaded.success || !loaded.result) {
                    log('error', loaded.error || 'Não consegui ler a conversa.', chatId)
                    return
                }
                const chat = loaded.result
                chatName = chat.name || chatName

                // Resposta desta conversa esperando a aprovação: nada de passo novo até a decisão
                // (depois dela a conversa volta à fila, se o contato tiver escrito de novo).
                if ([...parkedRef.current.values()].some(entry => entry.chatId === chatId)) {
                    log('info', 'Há uma resposta desta conversa esperando a sua aprovação.', chatId)
                    return
                }

                // Venda paga nesta conversa: há o que fazer (mandar a guia) mesmo sem
                // mensagem nova do contato.
                // Horário com parceiro ainda pendente com o supervisor: a guia espera a data,
                // para sair com "Agendado para" (docs/ai-user.md, "Fase 3").
                const scheduleQuestionPending = !!waitingRef.current
                    .get(chatId)
                    ?.question.startsWith(`${SUPERVISOR_REASON_LABEL.partner_schedule}: `)
                const paidHere = scheduleQuestionPending ? [] : (paidRef.current.get(chatId) ?? [])
                // Consumida aqui: vale para um passo só.
                const guided = guidedRef.current.delete(chatId)
                if (!handover && paidHere.length === 0 && !guided && !lastMessageIsFromContact(chat)) {
                    log('info', 'Nada a responder: a última mensagem não é do contato.', chatId)
                    return
                }
                const contactMessageId = lastContactMessageId(chat)
                if (
                    !handover &&
                    paidHere.length === 0 &&
                    !guided &&
                    contactMessageId != null &&
                    decidedRef.current.get(chatId) === contactMessageId
                ) {
                    log('info', 'Já decidi esta conversa e o contato não escreveu de novo.', chatId)
                    return
                }
                // Pergunta pendente com o supervisor: a conversa só segue se o contato
                // escreveu depois dela (assunto novo ou cobrança).
                const waitingEntry = waitingRef.current.get(chatId)
                const waitingPending = waitingEntry ? parsePendingQuestion(waitingEntry.question) : null
                if (paidHere.length === 0 && waitingEntry && contactMessagesSince(chat, waitingEntry.createdAt).length === 0) {
                    log('info', 'Esperando a sua orientação nesta conversa.', chatId)
                    return
                }

                log('info', 'Abrindo a conversa.', chatId)
                setCurrent({ chatId, chatName, stage: 'opening' })
                // Na tela cheia de atendimentos o dock não existe (a lista já é a
                // tela): ali a conversa abre na própria rota. No resto, abre no dock.
                const isFullScreenChat = () => window.location.pathname.startsWith('/atendimento/chats')
                const fullScreenChat = isFullScreenChat()
                const openConversation = () => {
                    if (isFullScreenChat()) {
                        navigate(`/atendimento/chats/${chatId}`)
                    } else {
                        openDock()
                        openChat(chatId)
                    }
                    return waitForScreenController(
                        'chat',
                        candidate => candidate.chatId === chatId,
                        SCREEN_TIMEOUT_MS,
                        controller.signal,
                    )
                }
                let screen = await openConversation()
                if (!alive()) return
                if (!screen) {
                    log('warning', 'A conversa não abriu a tempo. Tento de novo em um minuto.', chatId)
                    enqueue(chatId, RETRY_MS, chatName, handover)
                    return
                }
                setCurrent({ chatId, chatName, stage: 'thinking' })

                const markDecided = () => {
                    if (contactMessageId != null) decidedRef.current.set(chatId, contactMessageId)
                }

                // O funil do contato: oportunidades abertas nos funis da IA.
                const pipelines = profileRef.current?.pipelines ?? []
                const dealsResponse = await getAiUserConversationDeals(chatId)
                if (!alive()) return
                const deals: AiUserConversationDeal[] =
                    dealsResponse.success && Array.isArray(dealsResponse.result) ? dealsResponse.result : []
                const canMove =
                    !waitingEntry && deals.some(deal => allowedTargetStages(deal, pipelines).length > 0)

                log('info', 'Lendo a conversa e decidindo o que fazer.', chatId)
                // Vendas que já existem para o contato: sem isto, uma venda enviada sem a
                // mensagem de confirmação era agendada de novo no passo seguinte.
                const openSalesContext =
                    typeof chat.clientId === 'number'
                        ? describeOpenSales(await findOpenSales(chat.clientId).catch(() => []))
                        : ''
                if (!alive()) return
                const context = [buildFunnelContext(deals, pipelines), openSalesContext, describePaidFollowUps(paidHere)]
                    .filter(Boolean)
                    .join('\n\n')
                // O pedido do passo vai em dados: o texto que o envolve vem do painel MASTER.
                const turnStep = buildTurnStep(chat, userId, { handover, pending: waitingPending, context })
                const turnMessages: AiUserTurnMessage[] = []
                const baseActions = waitingEntry
                    ? WAITING_ACTIONS
                    : canMove
                      ? [...ACTIONS, AI_USER_ACTION.moveDealStage]
                      : ACTIONS
                const turnActions = [
                    ...baseActions,
                    ...(canSimulatePaymentRef.current ? [AI_USER_ACTION.simulatePayment] : []),
                    ...(ownAgendaRef.current ? [AI_USER_ACTION.appointmentFindSlots] : []),
                ]
                const turnImages = contactImageUrls(chat)
                const clientId = typeof chat.clientId === 'number' ? chat.clientId : null

                // Ações na tela de agendamento e a simulação de pagamento voltam ao modelo com o resultado; o passo
                // termina quando ele responde, consulta o supervisor ou não responde.
                let result: Extract<AiUserTurnResult, { success: true }>
                let salePixCode: string | null = null
                const deferredMoves: AiUserToolCall[] = []
                const stepAppointments: Array<{ kind: 'EXAM' | 'CONSULTATION' | 'PROCEDURE'; id: number }> = []
                for (let round = 0; ; round += 1) {
                    const turn = await aiUserTurn({
                        step: turnStep,
                        messages: turnMessages,
                        actions: turnActions,
                        chatId,
                        images: turnImages,
                    })
                    if (!alive()) return
                    if (!turn.success || !turn.result) {
                        log('error', turn.error || 'Não consegui falar com a IA.', chatId)
                        return
                    }
                    const roundResult = turn.result
                    if (!roundResult.success) {
                        if (roundResult.code === AI_CREDITS_EXHAUSTED_CODE) {
                            // Sem crédito, toda conversa da fila falharia igual: para tudo.
                            // O `finally` solta esta conversa; o laço para com o status.
                            log('error', `${roundResult.error} O Modo IA foi parado.`, chatId)
                            changeStatus('idle')
                            return
                        }
                        log('error', roundResult.error, chatId)
                        return
                    }
                    const roundCalls: AiUserToolCall[] = roundResult.message.toolCalls ?? []
                    // Só mover a etapa não encerra o passo: o contato ficaria sem resposta. O
                    // movimento é guardado e o modelo decide a resposta na rodada seguinte.
                    if (
                        roundCalls.length > 0 &&
                        roundCalls.every(entry => entry.function.name === AI_USER_ACTION.moveDealStage) &&
                        round < MAX_SCREEN_ROUNDS
                    ) {
                        deferredMoves.push(...roundCalls)
                        turnMessages.push({ role: 'assistant', content: roundResult.message.content, tool_calls: roundCalls })
                        for (const entry of roundCalls) {
                            turnMessages.push({
                                role: 'tool',
                                tool_call_id: entry.id,
                                content:
                                    'Anotado: a etapa muda quando você encerrar o passo. Agora decida o que fazer com o ' +
                                    'contato: send_message, ask_supervisor ou no_reply.',
                            })
                        }
                        continue
                    }
                    if (!roundCalls.some(entry => isRoundAction(entry.function.name))) {
                        result = roundResult
                        break
                    }
                    if (round >= MAX_SCREEN_ROUNDS) {
                        log('warning', 'A IA fez ações demais na tela sem decidir. Parei esta conversa; veja a tela.', chatId)
                        return
                    }
                    turnMessages.push({
                        role: 'assistant',
                        content: roundResult.message.content,
                        tool_calls: roundCalls,
                    })
                    setCurrent({ chatId, chatName, stage: 'working' })
                    for (const entry of roundCalls) {
                        const entryArgs = parseArguments(entry.function.arguments)
                        let content: string
                        if (isSchedulingAction(entry.function.name)) {
                            log('action', describeSchedulingCall(entry.function.name, entryArgs), chatId)
                            content = await executeSchedulingAction(entry, entryArgs, {
                                clientId,
                                pipelineId: deals[0]?.pipelineId ?? pipelines[0]?.id ?? null,
                                signal: controller.signal,
                                openScreen: path => {
                                    // Na tela cheia de atendimentos, sair dela fecha a
                                    // conversa: ela passa para o dock, que fica por cima.
                                    navigate(path)
                                    openDock()
                                    openChat(chatId)
                                },
                            })
                            if (!alive()) return
                        } else if (isAppointmentScheduleAction(entry.function.name)) {
                            log('action', describeAppointmentSchedule(entryArgs), chatId)
                            content = await executeAppointmentSchedule(entryArgs, { clientId })
                            if (!alive()) return
                        } else if (isAppointmentFindSlotsAction(entry.function.name)) {
                            log('action', describeAppointmentFindSlots(entryArgs), chatId)
                            content = await executeAppointmentFindSlots(entryArgs, { clientId })
                            if (!alive()) return
                        } else if (isBillingAction(entry.function.name)) {
                            log('action', describeBillingCall(entry.function.name, entryArgs), chatId)
                            content = await executeBillingAction(entry, entryArgs, controller.signal)
                            if (!alive()) return
                        } else if (isPaymentSimulationAction(entry.function.name)) {
                            log('action', describePaymentSimulation(entryArgs), chatId)
                            content = await executePaymentSimulation(entryArgs)
                            if (!alive()) return
                        } else {
                            // Responder junto com ações na tela seria decidir sem ver o
                            // resultado delas.
                            content = 'Não executado: decida depois de ver o resultado das ações na tela.'
                        }
                        // O Pix copia e cola do envio ou do salvar da cobrança: vai numa mensagem
                        // só com o código mesmo que o modelo esqueça o `pix_code`.
                        const pixCode = pixCodeFromResult(entry.function.name, content)
                        if (pixCode) salePixCode = pixCode
                        // Venda enviada: registra no servidor para mandar a guia quando o
                        // pagamento entrar. No grupo, os atendimentos se juntam até a cobrança.
                        const sale = saleFromResult(entry.function.name, content)
                        if (sale?.appointment) stepAppointments.push(sale.appointment)
                        if (sale?.billingId && stepAppointments.length > 0) {
                            const registered = await registerAiUserFollowUp(chatId, {
                                billingId: sale.billingId,
                                appointments: stepAppointments,
                            })
                            if (!registered.success) {
                                log('warning', `Não consegui registrar a venda para mandar a guia: ${registered.error}`, chatId)
                            }
                        }
                        turnMessages.push({ role: 'tool', tool_call_id: entry.id, content })
                    }
                    setCurrent({ chatId, chatName, stage: 'thinking' })
                }

                // Movimentos de etapa (podem vir junto da resposta), no quadro do funil, à
                // vista. Com mensagem ao contato, só DEPOIS do envio: se o supervisor descartar
                // a resposta, a etapa não muda (antes mudava e ficava mudada).
                const calls: AiUserToolCall[] = [...deferredMoves, ...(result.message.toolCalls ?? [])]
                const moves = calls.filter(entry => entry.function.name === AI_USER_ACTION.moveDealStage)
                const applyMoves = async (): Promise<boolean> => {
                    for (const move of moves) {
                        const moveArgs = parseArguments(move.function.arguments)
                        const dealId = Number(moveArgs.deal_id)
                        const stageId = Number(moveArgs.stage_id)
                        const reason = String(moveArgs.reason ?? '').trim()
                        const deal = deals.find(entry => entry.id === dealId)
                        if (!deal || !isAllowedMove(dealId, stageId, deals, pipelines)) {
                            log('warning', `A IA pediu um movimento fora do funil dela (oportunidade ${moveArgs.deal_id}, etapa ${moveArgs.stage_id}). Ignorado.`, chatId)
                            continue
                        }
                        const target = pipelines
                            .find(pipeline => pipeline.id === deal.pipelineId)
                            ?.stages.find(stage => stage.id === stageId)
                        log('decision', `Vou mover "${deal.title}" para "${target?.name ?? stageId}"${reason ? `: ${reason}` : '.'}`, chatId)
                        setCurrent({ chatId, chatName, stage: 'moving' })
                        // O quadro fica embaixo da conversa do dock: fecha a conversa, abre o
                        // funil, move o card e volta.
                        if (!fullScreenChat) closeChat()
                        navigate(`/sales/pipelines/${deal.pipelineId}`)
                        const board = await waitForScreenController(
                            'pipeline',
                            candidate => candidate.pipelineId === deal.pipelineId,
                            SCREEN_TIMEOUT_MS,
                            controller.signal,
                        )
                        if (!alive()) return false
                        const moved = board ? await board.moveDeal(dealId, stageId) : false
                        log(
                            moved ? 'action' : 'error',
                            moved
                                ? `Oportunidade movida para "${target?.name ?? stageId}".`
                                : 'Não consegui mover a oportunidade. Veja o aviso na tela.',
                            chatId,
                        )
                        if (!alive()) return false
                        screen = await openConversation()
                        if (!alive()) return false
                        if (!screen) {
                            log('warning', 'A conversa não reabriu depois do movimento.', chatId)
                            return false
                        }
                    }
                    return true
                }
                const delivered = (decision: ApprovalDecision | null) => decision === 'approve' || decision === 'sent'
                const skipMoves = () => {
                    if (moves.length > 0) log('info', 'Resposta não enviada: a oportunidade não mudou de etapa.', chatId)
                }

                const call = calls.find(entry => entry.function.name !== AI_USER_ACTION.moveDealStage)
                if (!call) {
                    if (moves.length > 0) {
                        if (!(await applyMoves())) return
                        markDecided()
                        return
                    }
                    const said = result.message.content?.trim()
                    log(
                        'warning',
                        `A IA não escolheu uma ação${said ? ` e escreveu: "${said}"` : ''}. Nada foi feito.`,
                        chatId,
                    )
                    return
                }

                /** Abre a conversa, escreve o texto (com os anexos) e envia. */
                const typeAndSend = async (text: string, files: File[]): Promise<boolean> => {
                    // A conversa pode ter mudado de lugar (tela cheia -> dock) durante as
                    // ações na tela de agendamento: pega o controlador de agora.
                    let view = getScreenController('chat')
                    if (!view || view.chatId !== chatId) view = await openConversation()
                    if (!alive()) return false
                    if (!view) {
                        log('warning', 'A conversa não reabriu para escrever a resposta.', chatId)
                        return false
                    }
                    setCurrent({ chatId, chatName, stage: 'typing' })
                    if (files.length > 0) view.attachFiles(files)
                    await view.typeDraft(text, controller.signal)
                    if (!alive()) return false
                    setCurrent({ chatId, chatName, stage: 'sending' })
                    const sent = await view.send()
                    log(sent ? 'action' : 'error', sent ? 'Mensagem enviada.' : 'O envio falhou. Veja o aviso na tela da conversa.', chatId)
                    return sent
                }

                /**
                 * Entrega a resposta ao contato. Sem a trava: escreve no campo e envia depois de
                 * um instante (dá para ler e parar). Com a trava: a resposta fica ESTACIONADA na
                 * gaveta, o motor segue para as outras conversas, e o envio volta à vez do motor
                 * quando o supervisor aprova. `skipApproval`: o Pix copia e cola, que é dado do
                 * sistema, sai junto da mensagem aprovada.
                 */
                const deliver = async (
                    text: string,
                    files: File[] = [],
                    options: { skipApproval?: boolean } = {},
                ): Promise<ApprovalDecision | null> => {
                    if (!requireApprovalRef.current || options.skipApproval) {
                        let view = getScreenController('chat')
                        if (!view || view.chatId !== chatId) view = await openConversation()
                        if (!alive()) return null
                        if (!view) {
                            log('warning', 'A conversa não reabriu para escrever a resposta.', chatId)
                            return null
                        }
                        setCurrent({ chatId, chatName, stage: 'typing' })
                        if (files.length > 0) view.attachFiles(files)
                        await view.typeDraft(text, controller.signal)
                        if (!alive()) return null
                        if (!options.skipApproval) {
                            // Envio automático: um instante para o supervisor ler e, se
                            // quiser, interromper com "Parar já" ou "Assumir".
                            await new Promise(resolve => setTimeout(resolve, AUTO_SEND_DELAY_MS))
                            if (!alive()) return 'aborted'
                        }
                        markDecided()
                        setCurrent({ chatId, chatName, stage: 'sending' })
                        const sent = await view.send()
                        log(sent ? 'action' : 'error', sent ? 'Mensagem enviada.' : 'O envio falhou. Veja o aviso na tela da conversa.', chatId)
                        return 'approve'
                    }

                    approvalSeqRef.current += 1
                    const approvalId = approvalSeqRef.current
                    const decided = new Promise<{ decision: ApprovalDecision; text?: string }>(resolve => {
                        const finish = (decision: ApprovalDecision, edited?: string) => {
                            if (!parkedRef.current.has(approvalId)) return
                            parkedRef.current.delete(approvalId)
                            setApprovals(prev => prev.filter(entry => entry.id !== approvalId))
                            resolve({ decision, text: edited })
                        }
                        parkedRef.current.set(approvalId, { chatId, resolve: finish })
                        controller.signal.addEventListener('abort', () => finish('aborted'), { once: true })
                    })
                    setApprovals(prev => [...prev, { id: approvalId, chatId, chatName, text, createdAt: Date.now() }])
                    log('info', 'Resposta pronta, esperando a sua aprovação. Sigo com as outras conversas.', chatId)
                    releaseMotor()

                    const { decision, text: edited } = await decided
                    if (decision === 'aborted') return 'aborted'
                    markDecided()
                    // Se o contato escreveu enquanto a resposta esperava, a conversa volta à fila.
                    enqueue(chatId, 0, chatName)
                    if (decision === 'discard') {
                        log('info', 'Resposta descartada pelo supervisor.', chatId)
                        return 'discard'
                    }
                    if (decision === 'sent') log('action', 'Mensagem enviada pelo supervisor na tela da conversa.', chatId)
                    // O que vem depois (Pix, guia, etapa) usa a tela: precisa da vez no motor.
                    if (!(await acquireMotor())) return 'aborted'
                    if (decision === 'sent') return 'sent'
                    const finalText = edited?.trim() || text
                    return (await typeAndSend(finalText, files)) ? 'approve' : null
                }

                const args = parseArguments(call.function.arguments)
                switch (call.function.name) {
                    case AI_USER_ACTION.sendMessage: {
                        const text = String(args.text ?? '').trim()
                        if (!text) {
                            log('warning', 'A IA quis responder, mas sem texto. Nada foi feito.', chatId)
                            return
                        }
                        log(
                            'decision',
                            waitingEntry ? 'Vou responder ao contato (a pergunta ao supervisor segue esperando).' : 'Vou responder ao contato.',
                            chatId,
                        )
                        // Venda paga: as guias vão anexadas nesta mensagem.
                        let guideFiles: File[] = []
                        if (paidHere.length > 0) {
                            try {
                                guideFiles = await fetchGuideFiles(paidHere.flatMap(entry => entry.appointments))
                            } catch {
                                log('error', 'Não consegui gerar a guia. A mensagem sai sem ela; veja o atendimento.', chatId)
                            }
                            if (!alive()) return
                        }
                        const decision = await deliver(text, guideFiles)
                        if (guideFiles.length > 0 && (decision === 'approve' || decision === 'sent')) {
                            for (const entry of paidHere) await completeAiUserFollowUp(entry.id)
                            paidRef.current.delete(chatId)
                            log('action', `Guia enviada (${guideFiles.length} arquivo(s)).`, chatId)
                        }
                        // Pix copia e cola numa mensagem só com o código, para o contato
                        // copiar sem levar junto o resto do texto. Só depois da confirmação.
                        const pixCode = String(args.pix_code ?? '').trim() || salePixCode || ''
                        if (pixCode && !text.includes(pixCode) && (decision === 'approve' || decision === 'sent') && alive()) {
                            await deliver(pixCode, [], { skipApproval: true })
                        }
                        if (!alive()) return
                        if (delivered(decision)) await applyMoves()
                        else skipMoves()
                        return
                    }
                    case AI_USER_ACTION.askSupervisor: {
                        const reason = toSupervisorReason(args.reason)
                        const question = String(args.question ?? '').trim() || 'A IA não soube como seguir.'
                        const asked = await askAiUserSupervisor(
                            chatId,
                            `${SUPERVISOR_REASON_LABEL[reason]}: ${question}`,
                        )
                        if (!asked.success || !asked.result) {
                            log('error', asked.error || 'Não consegui registrar a pergunta ao supervisor.', chatId)
                            return
                        }
                        markDecided()
                        waitingRef.current.set(chatId, {
                            ...asked.result,
                            chat: chatName ? { name: chatName } : null,
                        })
                        setWaiting(prev => [
                            ...prev.filter(entry => entry.id !== asked.result.id),
                            { ...asked.result, chat: chatName ? { name: chatName } : null },
                        ])
                        log('decision', `Esperando você (${SUPERVISOR_REASON_LABEL[reason]}): ${question}`, chatId)

                        // Horário, cancelamento e desistência: o contato recebe um aviso curto,
                        // como a equipe faz. Na dúvida de leitura ou de informação, nada vai.
                        const holding = String(args.holding_message ?? '').trim()
                        if (!AI_USER_HOLDING_REASONS.includes(reason)) {
                            if (holding) log('info', 'Aviso ao contato ignorado: em dúvida a IA fica calada.', chatId)
                            await applyMoves()
                            return
                        }
                        if (!holding) {
                            log('warning', 'A IA não escreveu o aviso ao contato. Ele fica sem resposta até você orientar.', chatId)
                            await applyMoves()
                            return
                        }
                        log('decision', 'Vou avisar o contato enquanto você verifica.', chatId)
                        const decision = await deliver(holding)
                        if (!alive()) return
                        if (delivered(decision)) await applyMoves()
                        else skipMoves()
                        return
                    }
                    case AI_USER_ACTION.noReply: {
                        const reason = String(args.reason ?? '').trim()
                        if (!(await applyMoves())) return
                        markDecided()
                        log('decision', `Não respondeu agora${reason ? `: ${reason}` : '.'}`, chatId)
                        return
                    }
                    default:
                        log('warning', `Ação desconhecida pedida pela IA: ${call.function.name}.`, chatId)
                }
            } catch (error: any) {
                if (alive()) log('error', error?.message || 'Erro inesperado no Modo IA.', chatId)
            } finally {
                // Só limpa se ainda for o dono do motor: "Parar já", "Assumir" e a resposta
                // estacionada já soltaram, e outro passo pode estar usando a tela.
                if (motorOwnerRef.current === motorToken && currentChatIdRef.current === chatId) finishCurrent()
            }
        },
        [userId, navigate, openDock, openChat, closeChat, log, enqueue, changeStatus, finishCurrent],
    )

    // O laço: uma conversa por vez, a de espera vencida mais antiga primeiro.
    useEffect(() => {
        if (status === 'idle') return
        const timer = setInterval(() => {
            if (statusRef.current !== 'running' || busyRef.current || resumeWaitersRef.current > 0) return
            const now = Date.now()
            const next = [...queueRef.current.values()]
                .filter(item => item.dueAt <= now)
                .sort((a, b) => a.dueAt - b.dueAt)[0]
            if (!next) return
            queueRef.current.delete(next.chatId)
            syncQueue()
            void process(next)
        }, TICK_MS)
        return () => clearInterval(timer)
    }, [status, process, syncQueue])

    /**
     * Conversas minhas esperando resposta entram na fila. `quiet` (a periódica e a do alerta
     * de atribuição): só as novas ou com mensagem nova desde a última varredura, e o diário só
     * fala quando entra alguma.
     */
    const sweep = useCallback(
        async (options: { quiet?: boolean } = {}) => {
            if (!companyId) return
            const response = await getChatByCompany(companyId, {
                scope: 'mine',
                status: 'OPEN',
                // Última fala do contato OU da triagem: a conversa que a triagem passou para
                // atendimento humano ("a equipe vai te informar") e foi movida para a IA está
                // esperando resposta, mas `unanswered` (só CLIENT) não a enxerga.
                lastSender: 'CLIENT,AI',
                limit: SWEEP_LIMIT,
            })
            if (!response.success || !Array.isArray(response.result)) {
                if (!options.quiet) log('warning', 'Não consegui buscar as conversas que esperam resposta.')
                return
            }
            const parked = new Set([...parkedRef.current.values()].map(entry => entry.chatId))
            let added = 0
            response.result.forEach((chat: any) => {
                const chatId = Number(chat?.id)
                if (!chatId) return
                const stamp = String(chat.lastMessageAt ?? chat.updatedAt ?? '')
                if (options.quiet) {
                    if (sweptStampRef.current.get(chatId) === stamp) return
                    if (queueRef.current.has(chatId) || currentChatIdRef.current === chatId || parked.has(chatId)) return
                }
                sweptStampRef.current.set(chatId, stamp)
                enqueue(chatId, added * 100, chat.name)
                added += 1
            })
            if (!options.quiet) log('info', `${added} conversa(s) esperando resposta entraram na fila.`)
            else if (added > 0) log('info', `${added} conversa(s) nova(s) para mim entraram na fila.`)
        },
        [companyId, enqueue, log],
    )
    sweepRef.current = sweep

    /**
     * Botão "Procurar conversas recebidas": as conversas dela, abertas, em que ela não
     * escreveu desde a última transferência. Entram na fila como passagem: ela lê tudo e
     * assume, mesmo que o contato nunca mais escreva.
     */
    const [findingHandovers, setFindingHandovers] = useState(false)
    const findHandovers = useCallback(async () => {
        setFindingHandovers(true)
        try {
            const response = await getAiUserHandoverCandidates()
            if (!response.success || !Array.isArray(response.result)) {
                log('error', response.error || 'Não consegui procurar as conversas recebidas.')
                return
            }
            const parked = new Set([...parkedRef.current.values()].map(entry => entry.chatId))
            const found = response.result.filter(
                entry => !parked.has(entry.chatId) && currentChatIdRef.current !== entry.chatId,
            )
            found.forEach((entry, index) => enqueue(entry.chatId, index * 100, entry.name ?? undefined, true))
            if (found.length === 0) log('info', 'Nenhuma conversa recebida esperando você.')
            else
                log(
                    'info',
                    `${found.length} conversa(s) recebida(s) sem resposta sua entraram na fila.` +
                        (statusRef.current === 'running' ? '' : ' Elas começam quando você iniciar.'),
                )
        } finally {
            setFindingHandovers(false)
        }
    }, [enqueue, log])

    // Enquanto trabalha, varre de minuto em minuto: conversa movida para a IA é assumida.
    useEffect(() => {
        if (status !== 'running') return
        const timer = setInterval(() => void sweep({ quiet: true }), SWEEP_INTERVAL_MS)
        return () => clearInterval(timer)
    }, [status, sweep])

    const start = useCallback(async () => {
        if (statusRef.current === 'running') return
        // Funil próprio é obrigatório: é nele que ficam as oportunidades que a IA atende.
        if (!profileRef.current || profileRef.current.pipelines.length === 0) {
            log(
                'error',
                'Este usuário de IA não é dono de nenhum funil. Peça a quem administra a conta para criar um funil e colocá-lo como dono.',
            )
            return
        }
        if (!(await acquireWorkLock())) {
            log('error', 'Este usuário de IA já está trabalhando em outra aba. Pare o Modo IA lá antes de iniciar aqui.')
            return
        }
        changeStatus('running')
        void loadWaiting()
        log(
            'info',
            requireApprovalRef.current
                ? 'Modo IA iniciado. Toda resposta espera a sua aprovação.'
                : 'Modo IA iniciado. As respostas saem sem aprovação.',
        )
        sweptStampRef.current.clear()
        void sweep()
        // Vendas pagas enquanto o Modo IA estava parado: a guia ainda não foi mandada.
        void loadPaid().then(entries => {
            const chats = [...new Set(entries.map(entry => entry.chatId))]
            chats.forEach((chatId, index) => enqueue(chatId, index * 100))
            if (chats.length > 0) log('info', `${chats.length} venda(s) paga(s) à espera da guia entraram na fila.`)
        })
    }, [changeStatus, log, sweep, loadWaiting, loadPaid, enqueue, acquireWorkLock])

    const pause = useCallback(() => {
        if (statusRef.current !== 'running') return
        if (!busyRef.current) {
            changeStatus('idle')
            log('info', 'Modo IA pausado.')
            return
        }
        changeStatus('pausing')
        log('info', 'Pausando: termino a conversa atual e paro.')
    }, [changeStatus, log])

    /** Interrompe o passo atual: aborta, limpa o rascunho escrito e solta a conversa. */
    const abortCurrent = useCallback(() => {
        runIdRef.current += 1
        abortRef.current?.abort()
        const chatId = currentChatIdRef.current
        if (chatId != null) {
            const screen = getScreenController('chat')
            if (screen?.chatId === chatId) screen.clearDraft()
        }
        currentChatIdRef.current = null
        busyRef.current = false
        motorOwnerRef.current = 0
        setCurrent(null)
        return chatId
    }, [])

    const stopNow = useCallback(() => {
        abortCurrent()
        // As respostas estacionadas não saem: parar é parar.
        const parked = [...parkedRef.current.values()]
        parked.forEach(entry => entry.resolve('aborted'))
        if (parked.length > 0) log('info', `${parked.length} resposta(s) esperando aprovação foram descartadas.`)
        queueRef.current.clear()
        syncQueue()
        changeStatus('idle')
        log('warning', 'Modo IA parado pelo supervisor.')
    }, [abortCurrent, syncQueue, changeStatus, log])

    const assumeCurrent = useCallback(() => {
        const chatId = abortCurrent()
        if (chatId == null) return
        assumedRef.current.add(chatId)
        queueRef.current.delete(chatId)
        syncQueue()
        log('info', 'O supervisor assumiu a conversa. A IA não volta a ela nesta sessão.', chatId)
        if (statusRef.current === 'pausing') changeStatus('idle')
    }, [abortCurrent, syncQueue, log, changeStatus])

    const decideApproval = useCallback((id: number, decision: 'approve' | 'discard', text?: string) => {
        parkedRef.current.get(id)?.resolve(decision, text)
    }, [])
    const setRequireApproval = useCallback(
        (value: boolean) => {
            requireApprovalRef.current = value
            setRequireApprovalState(value)
            try {
                window.localStorage.setItem(REQUIRE_APPROVAL_STORAGE_KEY, value ? '1' : '0')
            } catch {
                // Sem memória do navegador a escolha vale só até recarregar a aba.
            }
            log('info', value ? 'Respostas passam a esperar a sua aprovação.' : 'Respostas passam a sair sem aprovação.')
            // As que já estão esperando continuam esperando: cada uma pede a sua decisão.
        },
        [log],
    )

    const answerGuidance = useCallback(
        async (guidanceId: number, answer: string) => {
            const entry = waiting.find(item => item.id === guidanceId)
            const response = await answerAiUserGuidance(guidanceId, answer)
            if (!response.success || !response.result) {
                log('error', response.error || 'Não consegui registrar a orientação.', entry?.chatId)
                return false
            }
            const chatId = response.result.chatId
            setWaiting(prev => prev.filter(item => item.id !== guidanceId))
            waitingRef.current.delete(chatId)
            // A orientação é fato novo: a conversa volta a ser decidida, já com ela.
            decidedRef.current.delete(chatId)
            guidedRef.current.add(chatId)
            log('info', `Orientação recebida: ${answer.trim()}`, chatId)
            enqueue(chatId, 0, entry?.chat?.name ?? undefined)
            return true
        },
        [waiting, log, enqueue],
    )

    const value = useMemo<AiUserRunnerValue>(
        () => ({
            status,
            queue,
            current,
            approvals,
            waiting,
            diary,
            start,
            pause,
            stopNow,
            decideApproval,
            findHandovers,
            findingHandovers,
            assumeCurrent,
            answerGuidance,
            requireApproval,
            setRequireApproval,
            profile,
            profileError,
        }),
        [status, queue, current, approvals, waiting, diary, start, pause, stopNow, decideApproval, findHandovers, findingHandovers, assumeCurrent, answerGuidance, requireApproval, setRequireApproval, profile, profileError],
    )

    return <AiUserRunnerContext.Provider value={value}>{children}</AiUserRunnerContext.Provider>
}
